<template>
    <suspense>
      <template #default>
        <div class="app-layout">
          <NavBar v-if="isSignedIn && !isLoginPage"/>
          <main class="main-content" :class="{ 
            'with-sidebar': isSignedIn && !isLoginPage,
            'container': !isLoginPage 
          }">
            <router-view/>
          </main>
          <p-toast />
        </div>
      </template>
      <template #fallback>
        <div class="loading-container">
          <div class="loading-indicator"></div>
          <p>Loading application...</p>
        </div>
      </template>
    </suspense>
</template>
<script lang="ts">
import { Options } from 'vue-class-component';
import { base } from './misc/base';
import { mapState } from 'vuex';
import { State } from './store/index';
import { useToast } from 'primevue/usetoast';
import NavBar from './components/navbar.vue';

let toasty : any;

@Options({
  components: {
    NavBar,
  },
  computed: {
    ...mapState<State>({
      // eslint-disable-next-line
      ws: (state : State ) => state.ws, // Mapping the ws state to a computed property
      simforce: (state : State) => state.simforce // Mapping the API client
    })
  },
  watch: {
    ws: {
      handler(newWs) {
        // React to changes in the ws object

        if(!toasty || !newWs.message)
          return

        toasty.add({
          severity: 'warn',
          summary: newWs.message,
          life: 5000,
        });
        // Add any additional logic you want to handle when the ws state changes
      },
      deep: true // Necessary for deep watching of object properties
    }
  }
})
export default class App extends base {

  ws! : any;
  simforce!: any;
  // Create properties to track initialization status
  websocketInitialized = false;
  firstAuthVerification = false;

  mounted() {
    // Initialize toast regardless of page
    toasty = useToast();

    // Only initialize WebSocket if not on login page
    this.maybeInitWebSocket();
  }

  // Watch for route changes to handle WebSocket initialization
  beforeUpdate() {
    this.maybeInitWebSocket();
  }

  // Safely initialize app connections only when needed
  maybeInitWebSocket() {
    try {
      if (!this.isLoginPage && !this.websocketInitialized) {
        // Use Vuex auth state instead of manual storage checks
        const hasToken = this.isSignedIn;

        if (hasToken) {
          console.log('Initializing app connections');
          this.websocketInitialized = true;

          // Make sure store is available before dispatching
          if (this.store) {
            // Start the WebSocket
            try {
              this.store.dispatch('initWebSocket').catch(err => {
                console.warn('WebSocket initialization error (non-critical):', err);
                // Don't let this error break the app - WebSocket is optional
              });
            } catch (err) {
              console.warn('Error dispatching WebSocket init (non-critical):', err);
            }
          } else {
            console.warn('Store not available for WebSocket initialization');
          }

          // Verify our auth state only once - don't check repeatedly
          if (this.firstAuthVerification !== true) {
            this.firstAuthVerification = true;

            // Check auth with API after a small delay to ensure token setup is complete
            setTimeout(() => {
              console.log('Performing delayed authentication verification');
              // Make sure simforce is available before making API calls
              if (this.simforce) {
                // Make an API call to check authentication
                this.simforce.get('/me')
                  .then(() => console.log('Auth verification successful'))
                  .catch((e: Error) => {
                    console.warn('Auth verification warning:', e);
                    // Only handle critical auth errors, not initial configuration issues
                    // This prevents logout loops at application startup
                  });
              } else {
                console.warn('API client not available for authentication verification');
              }
            }, 1000);
          }
        }
      }
    } catch (err) {
      console.warn('Error in maybeInitWebSocket (non-critical):', err);
      // Don't let this break the app initialization
    }
  }

  get isLoginPage() {
    // Use this.route instead of this.$route to avoid warnings about reserved properties
    return this.route.path === '/login' ||
           this.route.path === '/forgot-password' ||
           this.route.path === '/reset-password';
  }

  get isSignedIn(): boolean {
    return this.store.getters.isSignedIn;
  }



}
</script>
<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

nav {
  padding: 30px;
}

nav a {
  font-weight: bold;
  color: #2c3e50;
}

nav a.router-link-exact-active {
  color: #333;
}

.cursor {
    cursor:pointer;
}

.bg-white {
    background:#fff;
}

:root {
	--popper-theme-background-color: #527296;
	--popper-theme-background-color-hover: #527296;
	--popper-theme-text-color: #ffffff;
	--popper-theme-border-width: 0px;
	--popper-theme-border-style: solid;
	--popper-theme-border-radius: 6px;
	--popper-theme-padding: 32px;
	--popper-theme-box-shadow: 0 6px 30px -6px rgba(0, 0, 0, 0.25);
}

.popper {
	font-size: 17px;
	font-weight: normal;
	z-index:10 !important;
}

/* Layout Styles */
.app-layout {
  min-height: 100vh;
}

.main-content {
  transition: margin-left 0.3s ease;
  min-height: calc(100vh - 60px);
}

.main-content.with-sidebar {
  margin-left: 280px;
  margin-top: 60px;
  padding: 1rem;
}

.main-content:not(.with-sidebar) {
  margin-top: 60px;
}

/* Mobile Layout */
@media (max-width: 768px) {
  .main-content.with-sidebar {
    margin-left: 0;
    padding: 0.5rem;
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100vw;
}

.loading-indicator {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #D0A441;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

</style>
